import React, { useCallback, useEffect, useMemo, useState } from 'react';

function formatTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return String(ts);
  }
}

export default function AdminChats() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [ip, setIp] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const pages = useMemo(() => {
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const load = useCallback(async (params) => {
    setLoading(true);
    setErr('');
    try {
      const url = new URL('/api/admin/chats', window.location.origin);
      url.searchParams.set('page', String(params.page));
      url.searchParams.set('page_size', String(pageSize));
      if (params.q) url.searchParams.set('q', params.q);
      if (params.ip) url.searchParams.set('ip', params.ip);

      const res = await fetch(url.pathname + url.search, { credentials: 'include' });
      if (!res.ok) {
        setErr('无法获取问答历史');
        setItems([]);
        setTotal(0);
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setErr('无法获取问答历史');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => { load({ q: '', ip: '', page: 1 }); }, [load]);

  const search = () => {
    setPage(1);
    load({ q, ip, page: 1 });
  };

  const go = (next) => {
    const p = Math.min(Math.max(1, next), pages);
    setPage(p);
    load({ q, ip, page: p });
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 className="h1">Chats</h2>
        <p className="subtle">问答历史（answer 默认折叠，点击展开）。</p>
      </div>

      <div className="glass cardPad">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <input
            className="input"
            placeholder="关键词（question/answer）"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <input
            className="input"
            placeholder="IP（精确/包含）"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            style={{ width: 220 }}
          />
          <button className="pillBtn" onClick={search} disabled={loading}>
            {loading ? '加载中…' : '搜索'}
          </button>
        </div>

        {err ? <div style={{ marginBottom: 12 }}><span className="tag tagWarn">{err}</span></div> : null}

        <table>
          <thead>
            <tr>
              <th>时间</th>
              <th>IP</th>
              <th>Question</th>
              <th>Answer</th>
              <th>下载简历</th>
              <th>标记</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isOpen = expanded.has(it.id);
              return (
                <tr key={it.id}>
                  <td className="small">{formatTime(it.created_at)}</td>
                  <td>
                    <div style={{ lineHeight: 1.2 }}>
                      <div>{it.ip}</div>
                      {it.ip_location ? (
                        <div className="small subtle" style={{ marginTop: 4 }}>
                          {it.ip_location}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{it.question}</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>
                    {it.answer ? (
                      <>
                        {isOpen ? it.answer : (it.answer.length > 140 ? it.answer.slice(0, 140) + '…' : it.answer)}
                        {it.answer.length > 140 ? (
                          <div style={{ marginTop: 8 }}>
                            <button className="collapseBtn" onClick={() => toggle(it.id)}>
                              {isOpen ? '收起' : '展开'}
                            </button>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="small">—</span>
                    )}
                  </td>
                  <td>
                    {it.resume_downloaded
                      ? <span className="tag tagSuccess">已下载</span>
                      : <span className="small">—</span>
                    }
                  </td>
                  <td>
                    {it.rate_limited ? <span className="tag tagWarn">rate limited</span> : <span className="tag">ok</span>}
                  </td>
                </tr>
              );
            })}
            {!items.length ? (
              <tr>
                <td colSpan={6} className="small" style={{ padding: 14 }}>暂无数据</td>
              </tr>
            ) : null}
          </tbody>
        </table>

        <div className="toolbar" style={{ marginTop: 12, justifyContent: 'space-between' }}>
          <div className="small">
            Total: {total} · Page {page}/{pages}
          </div>
          <div className="toolbar">
            <button className="pillBtn pillBtnGhost" onClick={() => go(page - 1)} disabled={loading || page <= 1}>Prev</button>
            <button className="pillBtn pillBtnGhost" onClick={() => go(page + 1)} disabled={loading || page >= pages}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}


